import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MunicipalitySelect } from "@/components/MunicipalitySelect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useSortableData, SortableHead } from "@/hooks/useSortableData";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Search, Pencil, Trash2, Inbox } from "lucide-react";
import { toast } from "sonner";
import { ExportButton } from "@/components/ExportButton";

const EMPTY = { date: "", ward_no: "", waste_identified: "", waste_removed: "" };

export default function LegacyWaste() {
  const { hasPerm } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [muni, setMuni] = useState({ district: "", name: "", type: "" });
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);

  const canManage = hasPerm("legacy_waste.manage");

  const load = () => {
    setLoading(true);
    api.get("/legacy-waste").then(({ data }) => setItems(data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return items;
    return items.filter((it) => [it.district, it.municipality_name, it.municipality_type, it.ward_no].some((v) => (v || "").toString().toLowerCase().includes(q)));
  }, [items, search]);

  const livePct = useMemo(() => {
    const t = Number(form.waste_identified), r = Number(form.waste_removed);
    return t > 0 ? Math.round((r / t) * 10000) / 100 : 0;
  }, [form.waste_identified, form.waste_removed]);

  const openNew = () => { setForm(EMPTY); setMuni({ district: "", name: "", type: "" }); setEditing({}); };
  const openEdit = (it) => {
    setForm({ date: it.date || "", ward_no: it.ward_no ?? "", waste_identified: it.waste_identified ?? "", waste_removed: it.waste_removed ?? "" });
    setMuni({ district: it.district || "", name: it.municipality_name || "", type: it.municipality_type || "" });
    setEditing(it);
  };
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!muni.district || !muni.name) { toast.error("Select district and municipal name"); return; }
    setSaving(true);
    const payload = {
      district: muni.district,
      municipality_name: muni.name,
      municipality_type: muni.type,
      date: form.date || null,
      ward_no: form.ward_no === "" ? null : Number(form.ward_no),
      waste_identified: form.waste_identified === "" ? null : Number(form.waste_identified),
      waste_removed: form.waste_removed === "" ? null : Number(form.waste_removed),
    };
    try {
      if (editing && editing.id) await api.put(`/legacy-waste/${editing.id}`, payload);
      else await api.post("/legacy-waste", payload);
      toast.success(editing && editing.id ? "Updated" : "Added");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    try { await api.delete(`/legacy-waste/${deleteItem.id}`); toast.success("Deleted"); setDeleteItem(null); load(); }
    catch { toast.error("Failed to delete"); }
  };

  const num = (v) => (v == null || v === "" ? "—" : Number(v).toLocaleString("en-IN"));

  const sort = useSortableData(filtered);
  const sorted = sort.sorted;

  return (
    <div data-testid="legacy-waste-page">
      <div className="flex justify-between items-start mb-6 gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-emerald-600 font-medium">Monitoring</p>
          <h1 className="font-heading text-2xl font-bold text-slate-900 tracking-tight">Legacy Waste Remediation</h1>
          <p className="text-sm text-slate-500 mt-1">{items.length} record(s) of legacy waste remediation.</p>
        </div>
        <div className="flex items-center gap-2">
          <ExportButton items={items} filename="legacy-waste" testId="legacy-waste-export-btn" />
          {canManage && (
          <Button onClick={openNew} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="legacy-waste-add-btn">
            <Plus className="h-4 w-4 mr-2" /> Add New
          </Button>
          )}
        </div>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <div className="p-4 border-b border-slate-200">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by district, municipality, ward..." className="pl-9" data-testid="legacy-waste-search-input" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">S.No</TableHead>
                <SortableHead column="district" sort={sort}>District</SortableHead>
                <SortableHead column="municipality_name" sort={sort}>Municipality</SortableHead>
                <SortableHead column="municipality_type" sort={sort}>Type</SortableHead>
                <SortableHead column="date" sort={sort}>Date</SortableHead>
                <SortableHead column="ward_no" sort={sort} className="text-right" right>Ward</SortableHead>
                <SortableHead column="waste_identified" sort={sort} className="text-right" right>Legacy Waste Identified</SortableHead>
                <SortableHead column="waste_removed" sort={sort} className="text-right" right>Legacy Waste Removed</SortableHead>
                <SortableHead column="percentage" sort={sort} className="text-right" right>Percentage</SortableHead>
                {canManage && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={canManage ? 10 : 9} className="text-center py-10 text-slate-400">Loading...</TableCell></TableRow>
              ) : sorted.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canManage ? 10 : 9} className="text-center py-12">
                    <Inbox className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                    <p className="text-slate-500">No legacy waste records yet.</p>
                  </TableCell>
                </TableRow>
              ) : (
                sorted.map((it, idx) => (
                  <TableRow key={it.id} className="hover:bg-slate-50" data-testid={`legacy-waste-row-${it.id}`}>
                    <TableCell className="text-slate-500">{idx + 1}</TableCell>
                    <TableCell className="capitalize">{(it.district || "—").toLowerCase()}</TableCell>
                    <TableCell className="font-medium">{it.municipality_name || "—"}</TableCell>
                    <TableCell className="text-xs text-slate-600">{it.municipality_type || "—"}</TableCell>
                    <TableCell>{it.date ? new Date(it.date).toLocaleDateString() : "—"}</TableCell>
                    <TableCell className="text-right">{it.ward_no ?? "—"}</TableCell>
                    <TableCell className="text-right tabular-nums">{num(it.waste_identified)}</TableCell>
                    <TableCell className="text-right tabular-nums">{num(it.waste_removed)}</TableCell>
                    <TableCell className="text-right">
                      <span className="inline-flex items-center rounded-full bg-emerald-50 text-emerald-700 font-semibold px-2.5 py-1 text-xs">{it.percentage != null ? `${it.percentage}%` : "—"}</span>
                    </TableCell>
                    {canManage && (
                      <TableCell className="text-right whitespace-nowrap">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(it)} data-testid={`legacy-waste-edit-${it.id}`}><Pencil className="h-4 w-4 text-slate-600" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => setDeleteItem(it)} data-testid={`legacy-waste-delete-${it.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="legacy-waste-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing && editing.id ? "Edit Legacy Waste Record" : "Add Legacy Waste Record"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <MunicipalitySelect value={muni} onChange={setMuni} testId="legacy-waste-muni" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Date</Label>
                <Input type="date" value={form.date} onChange={(e) => setField("date", e.target.value)} data-testid="legacy-waste-input-date" />
              </div>
              <div className="space-y-1.5">
                <Label>Ward Number</Label>
                <Input type="number" value={form.ward_no} onChange={(e) => setField("ward_no", e.target.value)} data-testid="legacy-waste-input-ward" />
              </div>
              <div className="space-y-1.5">
                <Label>Total Legacy Waste Identified</Label>
                <Input type="number" value={form.waste_identified} onChange={(e) => setField("waste_identified", e.target.value)} data-testid="legacy-waste-input-identified" />
              </div>
              <div className="space-y-1.5">
                <Label>Total Legacy Waste Removed</Label>
                <Input type="number" value={form.waste_removed} onChange={(e) => setField("waste_removed", e.target.value)} data-testid="legacy-waste-input-removed" />
              </div>
              <div className="space-y-1.5">
                <Label>Percentage (auto)</Label>
                <Input value={`${livePct}%`} readOnly className="bg-slate-50 font-semibold text-emerald-700" data-testid="legacy-waste-input-percentage" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} data-testid="legacy-waste-cancel-btn">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="legacy-waste-save-btn">{saving ? "Saving..." : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteItem} onOpenChange={(o) => !o && setDeleteItem(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this legacy waste record?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="legacy-waste-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-500 hover:bg-red-600" data-testid="legacy-waste-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
